import { PressEvent } from "@react-types/shared";
import { TourElement } from "core/types";
import { Button, ButtonProps, Spinner } from "ds/ui";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  CircleAlertIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import {
  ElementType,
  HTMLAttributes,
  useCallback,
  useMemo,
  useState,
} from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { useTranslations } from "translations";

type BannerVariants = VariantProps<typeof bannerVariants>;
type BannerColor = Exclude<BannerVariants["color"], undefined>;

export type BannerAction = Pick<
  ButtonProps,
  | "as"
  | "href"
  | "isDisabled"
  | "isLoading"
  | "startContent"
  | "endContent"
  | "onPress"
  | "id"
> & {
  tour?: TourElement;
  label: string;
};

type BannerPropsBase = HTMLAttributes<HTMLDivElement> & {
  actions?: BannerAction[];
  heading?: string;
  message: React.ReactNode | string;
  messageTour?: TourElement;
  ref?: React.Ref<HTMLDivElement>;
  isLoading?: boolean;
  withIcon?: boolean;
  icon?: ElementType;
} & BannerVariants;

type BannerPropsWithClose = BannerPropsBase & {
  onClose: (e?: PressEvent) => void;
  withClose: boolean;
};

type BannerPropsWithoutClose = BannerPropsBase & {
  onClose?: never;
  withClose?: boolean;
};

export type BannerProps = BannerPropsWithClose | BannerPropsWithoutClose;

const DEFAULT_COLOR = "primary";
const ICONS: Record<BannerColor, ElementType> = {
  danger: AlertTriangleIcon,
  primary: InfoIcon,
  success: CheckCircleIcon,
  warning: CircleAlertIcon,
};

const bannerVariants = tv({
  defaultVariants: {
    color: DEFAULT_COLOR,
    size: "md",
  },
  slots: {
    bannerActions: "ml-3 flex items-center gap-6 overflow-visible",
    bannerContent: "flex flex-col flex-1 gap-2",
    bannerheading: "font-semibold",
    bannerMessage: "text-body-md",
    icon: "",
    wrapper:
      "flex flex-row rounded-lg border border-solid border-gray-500 p-4 space-between w-full items-center gap-3",
  },
  variants: {
    color: {
      danger: { icon: "text-danger-dark", wrapper: "bg-danger-light" },
      primary: {
        icon: "text-primary",
        wrapper: "bg-primary-light",
      },
      success: {
        icon: "text-success-dark",
        wrapper: "bg-success-light",
      },
      warning: {
        icon: "text-warning-dark",
        wrapper: "bg-warning-light",
      },
    },
    size: {
      md: {
        icon: "h-5 w-5",
      },
      sm: {
        icon: "h-4 w-4",
      },
    },
  },
});

export function Banner({
  actions,
  color = "primary",
  heading,
  icon: customIcon,
  isLoading,
  message,
  messageTour,
  onClose,
  ref,
  size,
  withClose,
  withIcon = true,
  ...rest
}: BannerProps) {
  const [open, setOpen] = useState(true);
  const translations = useTranslations();

  const variantClasses = useMemo(
    () =>
      bannerVariants({
        color,
        size,
      }),
    [color, size],
  );

  const {
    bannerActions,
    bannerContent,
    bannerheading,
    bannerMessage,
    icon,
    wrapper,
  } = variantClasses;

  const hasActions = useMemo(() => !!actions?.length, [actions?.length]);

  const firstActionFocusableElement = useFocusElement<HTMLButtonElement>({
    dependencies: [withClose],
    shouldFocus: hasActions,
  });

  const closeButtonFocusableElement = useFocusElement<HTMLButtonElement>({
    dependencies: [withClose],
    shouldFocus: withClose === true && !hasActions,
  });

  const handleClose = useCallback(
    (e?: PressEvent) => {
      setOpen(false);
      onClose?.(e);
    },
    [onClose],
  );

  const Container = useMemo(
    () => (typeof message === "string" ? "p" : "div"),
    [message],
  );

  const Icon = useMemo(() => customIcon ?? ICONS[color], [customIcon, color]);

  const closeButtonAriaLabel = useMemo(
    () => translations.actions.close,
    [translations.actions.close],
  );

  if (!open) return null;

  return (
    <div ref={ref} {...rest} className={wrapper()}>
      {!!withIcon &&
        (isLoading ? (
          <Spinner color={color} size="sm" testId="banner-spinner" />
        ) : Icon ? (
          <Icon className={icon()} />
        ) : null)}
      {}
      <div className={bannerContent()}>
        {heading && <p className={bannerheading()}>{heading}</p>}
        <Container className={bannerMessage()} {...messageTour}>
          {message}
        </Container>
      </div>
      {!!(hasActions || withClose) && (
        <div className={bannerActions()}>
          {hasActions &&
            actions!.map((action, index) => {
              return (
                <Button
                  key={action.id}
                  ref={
                    index === 0
                      ? (firstActionFocusableElement as any)
                      : undefined
                  }
                  as={action.as}
                  color={color}
                  endContent={action.endContent}
                  href={action.href}
                  id={action.id}
                  isDisabled={action.isDisabled}
                  isLoading={action.isLoading}
                  size="sm"
                  startContent={action.startContent}
                  tour={action.tour}
                  variant="solid"
                  onPress={action.onPress}
                >
                  {action.label}
                </Button>
              );
            })}
          {!!withClose && (
            <Button
              ref={closeButtonFocusableElement as any}
              aria-label={closeButtonAriaLabel}
              color="black"
              id="banner-close"
              isIconOnly
              size="xs"
              variant="light"
              onPress={handleClose}
            >
              <XIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

Banner.displayName = "RecareUI.Banner";
